import React, { useCallback, useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    AssignManagingUserPayload,
    getOrganizationManagingUsers,
    ManagingUserOption,
} from '../api/individuals.api';
import {
    PRIMARY_SPORT_OPTIONS,
    PrimarySportOptionId,
    mapOptionIdToPrimarySportValue,
    mapPrimarySportValueToOptionId,
} from 'utils/primarySport';

interface AssignManagingUserDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (payload: AssignManagingUserPayload) => void;
    organizationUuid: string;
    individualName: string;
    submitting?: boolean;
    defaultPrimarySport?: string | null;
}

const AssignManagingUserDialog: React.FC<AssignManagingUserDialogProps> = ({
    open,
    onClose,
    onSubmit,
    organizationUuid,
    individualName,
    submitting = false,
    defaultPrimarySport,
}) => {
    const [tabIndex, setTabIndex] = useState(0);

    const [existingUsers, setExistingUsers] = useState<ManagingUserOption[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState<ManagingUserOption | null>(null);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [userName, setUserName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const defaultSportOptionId = mapPrimarySportValueToOptionId(defaultPrimarySport);
    const [primarySport, setPrimarySport] = useState<PrimarySportOptionId>(defaultSportOptionId);
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

    const fetchExistingUsers = useCallback(() => {
        if (!organizationUuid) return;
        setLoadingUsers(true);
        getOrganizationManagingUsers(organizationUuid)
            .then((res) => {
                setExistingUsers(res.data?.data ?? res.data ?? []);
            })
            .catch(() => {
                setExistingUsers([]);
            })
            .finally(() => {
                setLoadingUsers(false);
            });
    }, [organizationUuid]);

    useEffect(() => {
        if (open && tabIndex === 0) {
            fetchExistingUsers();
        }
    }, [open, tabIndex, fetchExistingUsers]);

    const resetForm = () => {
        setTabIndex(0);
        setSelectedUser(null);
        setFirstName('');
        setLastName('');
        setUserName('');
        setEmail('');
        setPhone('');
        setPassword('');
        setShowPassword(false);
        setPrimarySport(defaultSportOptionId);
        setFormErrors({});
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmitExisting = () => {
        if (!selectedUser) return;
        onSubmit({ user_uuid: selectedUser.uuid });
    };

    const handleSubmitNew = () => {
        const errors: { [key: string]: string } = {};

        if (!firstName.trim()) {
            errors.firstName = 'First name is required';
        }
        if (!lastName.trim()) {
            errors.lastName = 'Last name is required';
        }
        if (!userName || userName.trim().length < 5) {
            errors.userName = 'Username must be at least 5 characters';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(userName)) {
            errors.userName = 'Username can only contain letters, numbers, underscores, and dashes';
        }
        if (!password || password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        }

        const hasEmail = email.trim().length > 0;
        const hasPhone = phone.trim().length > 0;

        if (!hasEmail && !hasPhone) {
            errors.email = 'At least one of email or phone is required';
            errors.phone = 'At least one of email or phone is required';
        }
        if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (hasPhone) {
            const digits = phone.replace(/\D/g, '');
            if (digits.length < 10) {
                errors.phone = 'Phone number must have at least 10 digits';
            }
        }

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;

        const selectedSportValue = mapOptionIdToPrimarySportValue(primarySport);

        let normalizedPhone = '';
        if (hasPhone) {
            const digits = phone.replace(/\D/g, '');
            normalizedPhone = digits.length === 10 ? `+1${digits}` : `+${digits}`;
        }

        const payload: AssignManagingUserPayload = {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            user_name: userName.trim(),
            password,
            ...(hasEmail ? { email: email.trim() } : {}),
            ...(hasPhone ? { phone: normalizedPhone } : {}),
            ...(selectedSportValue ? { primary_sport: selectedSportValue } : {}),
        };

        onSubmit(payload);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Assign Managing User to {individualName}</DialogTitle>
            <DialogContent>
                <Tabs value={tabIndex} onChange={(_, newVal) => setTabIndex(newVal)} sx={{ mb: 2 }}>
                    <Tab label="Select Existing User" />
                    <Tab label="Create New User" />
                </Tabs>

                {tabIndex === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Select from users who already manage an individual in this organization.
                        </Typography>
                        <Autocomplete
                            options={existingUsers}
                            loading={loadingUsers}
                            value={selectedUser}
                            onChange={(_, newValue) => setSelectedUser(newValue)}
                            getOptionLabel={(option) =>
                                `${option.first_name} ${option.last_name} (${option.user_name})`
                            }
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search existing users"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingUsers ? (
                                                    <CircularProgress color="inherit" size={20} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.uuid}>
                                    <Box>
                                        <Typography variant="body1">
                                            {option.first_name} {option.last_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            @{option.user_name}
                                            {option.email ? ` · ${option.email}` : ''}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            noOptionsText={
                                loadingUsers
                                    ? 'Loading...'
                                    : 'No existing managing users in this organization'
                            }
                        />
                    </Box>
                )}

                {tabIndex === 1 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Create a new user account. At least one of email or phone is required.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="First Name *"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                error={!!formErrors.firstName}
                                helperText={formErrors.firstName}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Last Name *"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                error={!!formErrors.lastName}
                                helperText={formErrors.lastName}
                                fullWidth
                                size="small"
                            />
                        </Box>
                        <TextField
                            label="Username *"
                            value={userName}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '');
                                setUserName(value);
                            }}
                            error={!!formErrors.userName}
                            helperText={
                                formErrors.userName ||
                                'Minimum 5 characters, letters, numbers, underscores, dashes'
                            }
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            error={!!formErrors.phone}
                            helperText={formErrors.phone}
                            fullWidth
                            size="small"
                            placeholder="(619) 467-6855"
                        />
                        <TextField
                            label="Password *"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={!!formErrors.password}
                            helperText={formErrors.password || 'Minimum 8 characters'}
                            fullWidth
                            size="small"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel id="primary-sport-label">
                                Primary Sport (Optional)
                            </InputLabel>
                            <Select
                                labelId="primary-sport-label"
                                label="Primary Sport (Optional)"
                                value={primarySport}
                                onChange={(e) =>
                                    setPrimarySport(e.target.value as PrimarySportOptionId)
                                }
                            >
                                {PRIMARY_SPORT_OPTIONS.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                {tabIndex === 0 ? (
                    <Button
                        onClick={handleSubmitExisting}
                        variant="contained"
                        color="primary"
                        disabled={!selectedUser || submitting}
                    >
                        {submitting ? 'Assigning...' : 'Assign User'}
                    </Button>
                ) : (
                    <Button
                        onClick={handleSubmitNew}
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                    >
                        {submitting ? 'Creating...' : 'Create & Assign'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AssignManagingUserDialog;
